import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./index.module.scss";
import {
    formatTimeDuration,
    isDayOf,
    isInFuture,
    localizeTime
} from "helpers/time";
import DownloadIcon from "assets/icons/download.svg?react";
import TrashIcon from "assets/icons/trash-sm.svg?react";
import ShareIcon from "assets/icons/share.svg?react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { exists } from "helpers/booleans";
import { useTranslation } from "react-i18next";
import { Button } from "components/buttons/Button";
import { GatedContentDisabledTooltip } from "components/tooltips/GatedContentDisabledTooltip";
import { GatedContentStatus } from "store/stripeConnectAccount/types";
import { shorten } from "helpers/strings";
import PencilIcon from "assets/icons/pencil-simple.svg?react";
import { ContextMenu } from "components/inputs/context-menu";
import { useIsMobile } from "hooks/useIsMobile";
import { VideoPlaybackModal } from "components/modal/VideoPlaybackModal";
import { DownloadProps, useVideoDownload } from "hooks/useVideoDownload";
import { VideoDownloadStatus } from "components/recording-card/RecordingCard/types";
import {
    ContextMenuItem,
    ContextMenuWrapper
} from "components/inputs/context-menu-button";
import { useClipboard } from "hooks/useClipboard";
import { BaseDetails, DetailsVariant } from "../BaseDetails";
import { BroadcastThumbnail } from "components/thumbnails/BroadcastThumbnail";
import { DetailRow } from "../BaseDetails/DetailRow";
import { ComponentMap } from "components/utility/ComponentMap";
import { useClaimCheck } from "hooks/useClaimCheck";
import { useVideoUpload } from "hooks/useVideoUpload";
import { VideoUploadStatus } from "store/uploads/types";
import { useInterval } from "hooks/useInterval";
import {
    BroadcastResponse,
    CreatorEntitlementResponse
} from "@switcherstudio/api-core-client";
import { VideoBadgesV2 } from "./VideoBadgesV2";
import { trackEvent } from "helpers/analyticsHelpers";

dayjs.extend(relativeTime);

export enum DisabledVariant {
    NoInteract = "NoInteract",
    Disabled = "Disabled",
    Processing = "Processing"
}
export interface BroadcastResponseDetailsProps {
    broadcast: BroadcastResponse;
    badges?: boolean;
    setPricingSelectModalIsOpen?: (...args: any) => any;
    setExistingEntitlement?: (...args: any) => any;
    gatedContentStatus?: GatedContentStatus;
    showPricing?: boolean;
    productEntitlement?: CreatorEntitlementResponse;
    showEdit?: boolean;
    onBroadcastUpdate?: () => void;
    onEdit?: (broadcast: BroadcastResponse) => void;
    handleDeleteBroadcast?: () => void;
    location?: "video-library" | "player-playlist" | "practice";
    allowVideoPlaybackOnThumbnailClick?: boolean;
    showDownload?: boolean;
    entitlements?: CreatorEntitlementResponse[] | undefined | null;
    disabled?: boolean;
    disabledVariant?: DisabledVariant;
    isDraggable?: boolean;
    variant?: DetailsVariant;
}

export const BroadcastResponseDetails = ({
    broadcast,
    badges,
    setPricingSelectModalIsOpen,
    setExistingEntitlement,
    gatedContentStatus,
    showPricing = false,
    productEntitlement,
    showEdit = false,
    showDownload = false,
    onEdit = () => {},
    handleDeleteBroadcast,
    location,
    allowVideoPlaybackOnThumbnailClick,
    entitlements,
    disabled = false,
    disabledVariant = DisabledVariant.Disabled,
    isDraggable = false,
    variant = DetailsVariant.Horizontal
}: BroadcastResponseDetailsProps) => {
    const video = broadcast.Videos?.[0];
    const metrics = broadcast.Analytics;

    const { t } = useTranslation();
    const hasCatalogClaim = useClaimCheck("catalog");
    const { handleDownload, getDownloadStatus, getDownloadProgress } =
        useVideoDownload();
    const { copy } = useClipboard();
    const { getUploadStatus } = useVideoUpload(
        broadcast?.Details?.Id,
        video?.Details?.Status as VideoUploadStatus
    );
    const [videoPlaybackModalIsOpen, setVideoPlaybackModalIsOpen] =
        useState(false);

    const downloadStatus = useMemo<VideoDownloadStatus>(
        () =>
            getDownloadStatus({
                videoId: video?.Details?.Uid
            } as DownloadProps),
        [video, getDownloadStatus]
    );

    const percentComplete = useMemo<number>(
        () =>
            getDownloadProgress({
                videoId: video?.Details?.Uid
            } as DownloadProps),
        [getDownloadProgress, video?.Details?.Uid]
    );

    const [timerEndTime, setTimerEndTime] = useState<Date>();
    const [timeRemaining, setTimeRemaining] = useState<number>(
        timerEndTime && timerEndTime?.getTime() < new Date().getTime()
            ? timerEndTime?.getTime() - new Date().getTime()
            : 0
    );
    const [triggerThumbnailUpdate, setTriggerThumbnailUpdate] =
        useState<number>(0);

    const videoState = useMemo<VideoUploadStatus>(() => {
        const isInitialStatusProcessingOrQueued =
            video?.Details?.Status === VideoUploadStatus.Processing ||
            video?.Details?.Status === VideoUploadStatus.Queued;
        if (isInitialStatusProcessingOrQueued)
            return getUploadStatus({ broadcastId: broadcast?.Details?.Id });

        return VideoUploadStatus.Success;
    }, [video?.Details?.Status, getUploadStatus, broadcast?.Details?.Id]);

    const isVideoProcessing = useMemo<boolean>(
        () => videoState !== VideoUploadStatus.Success,
        [videoState]
    );

    // This is used to trigger a thumbnail refresh when the scheduled event timer reaches 0.
    const triggerThumbnailRefresh = useCallback(() => {
        setTriggerThumbnailUpdate((prev) => prev + 1);
    }, [setTriggerThumbnailUpdate]);

    // This is used to calculate the time remaining for the scheduled event timer.
    const calcTimeRemaining = (timeEnd: Date) => {
        if (!timeEnd) return;
        const now = new Date().getTime();
        const distance = timeEnd.getTime() - now;

        setTimeRemaining(distance > 0 ? distance : 0);
    };

    // This will initialize the timer for the scheduled event when timerEndTime is set.
    useInterval(
        () => {
            calcTimeRemaining(timerEndTime);
        },
        timeRemaining > 0 ? 1000 : null
    );

    // Initial run of calcuation before interval begins.
    useEffect(() => {
        calcTimeRemaining(timerEndTime);
    }, [timerEndTime]);

    // Used to trigger refreshing some data (IE: thumbnails)
    // when the when the scheduled event timer reaches 0.
    useEffect(() => {
        if (timeRemaining > 0) return;

        triggerThumbnailRefresh();
    }, [timeRemaining, triggerThumbnailRefresh]);

    // If the broadcast is scheduled to premiere and is in the future,
    // it is considered a scheduled upload.
    const isScheduledUpload = useMemo(() => {
        let isScheduled =
            exists(broadcast?.Details?.StartsAt) &&
            isInFuture(broadcast?.Details?.StartsAt) &&
            broadcast?.Details?.BroadcastStatus === "Ended";

        return isScheduled;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [broadcast, triggerThumbnailUpdate]);

    // If the broadcast is scheduled to go live any time and has not started yet,
    // it is considered a scheduled live broadcast.
    const isScheduledLive = useMemo(() => {
        let isScheduled =
            exists(broadcast?.Details?.StartsAt) &&
            broadcast?.Details?.BroadcastStatus === "Ready";

        return isScheduled;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [broadcast, triggerThumbnailUpdate]);

    // if broadcast is scheduled to premiere, check toggle value
    const showPremiereCountdown = useMemo(() => {
        return broadcast?.Details?.ShowPremiereCountdown;
    }, [broadcast]);

    useEffect(() => {
        const isScheduled = isScheduledUpload || isScheduledLive;
        if (isScheduled && isInFuture(broadcast?.Details?.StartsAt)) {
            setTimerEndTime(new Date(broadcast?.Details?.StartsAt));
        }
    }, [broadcast, isScheduledLive, isScheduledUpload]);

    const startDate = useMemo(() => {
        if (!broadcast) return null;
        if (isScheduledUpload || isScheduledLive) {
            return new Date(broadcast?.Details.StartsAt);
        }
        return broadcast?.Details.ActiveAt
            ? new Date(broadcast?.Details.ActiveAt)
            : new Date(broadcast?.Details.CreatedAt);
    }, [broadcast, isScheduledLive, isScheduledUpload]);

    const dayOfBroadcast = useMemo(() => {
        return isDayOf(startDate?.toString());
    }, [startDate]);

    const localizedTime = useMemo(() => {
        return localizeTime(startDate?.toString(), "h:mm A");
    }, [startDate]);

    const thumbnailImageURL = useMemo(
        () =>
            broadcast?.Thumbnail?.Details?.Url ||
            (exists(video) ? video?.Details?.Thumbnail : ""),
        [broadcast, video]
    );

    const mobileCheck = useIsMobile();

    //This only disables the share button if a video is unpublished or archived. It does not prevent sharing of live videos.
    const shareable = useMemo(() => {
        return (
            broadcast?.Details?.BroadcastStatus !== "Unpublished" &&
            broadcast?.Details?.BroadcastStatus !== "Archived"
        );
    }, [broadcast?.Details?.BroadcastStatus]);

    const isUnpublished = useMemo(() => {
        return broadcast?.Details?.BroadcastStatus === "Archived";
    }, [broadcast?.Details?.BroadcastStatus]);

    const shareOptions = useMemo<ContextMenuItem[]>(() => {
        return [
            {
                key: "copy-code",
                text: t("buttons:copy-embed-code"),
                onClick: () => copy(broadcast?.MetaData?.EmbedCode)
            },
            {
                key: "copy-link",
                text: t("buttons:copy-share-link"),
                onClick: () => copy(broadcast?.MetaData?.ShareUrl)
            }
        ];
    }, [broadcast, copy, t]);

    const mobileButtonsMenuItems = useMemo(() => {
        if (disabled) {
            return [
                {
                    key: "Delete",
                    text: t("buttons:delete"),
                    onClick: handleDeleteBroadcast
                }
            ];
        }

        const menuItems = [
            {
                key: "Edit",
                text: t("buttons:edit"),
                onClick: () => onEdit?.(broadcast)
            }
        ];

        if (location === "video-library") {
            menuItems.push(...shareOptions);

            if (showDownload) {
                menuItems.push({
                    key: "Download",
                    text: t("buttons:download"),
                    onClick: async () => {
                        await handleDownload({
                            broadcastId: broadcast?.Details.Id,
                            videoId: video?.Details?.Uid,
                            title: broadcast?.Details.Title
                        } as DownloadProps);
                    }
                });
            }

            menuItems.push({
                key: "Delete",
                text: t("buttons:delete"),
                onClick: handleDeleteBroadcast
            });
        }

        return menuItems;
    }, [
        disabled,
        t,
        location,
        handleDeleteBroadcast,
        onEdit,
        broadcast,
        shareOptions,
        showDownload,
        handleDownload,
        video?.Details?.Uid
    ]);

    const broadcastDetailsItems = useMemo(
        () => [
            ...(isUnpublished && location !== "practice"
                ? [
                      <span key="status" className={styles["status"]}>
                          {t("widgets:Unpublished")}
                      </span>
                  ]
                : []),
            ...(!isScheduledUpload &&
            !isScheduledLive &&
            location !== "practice"
                ? [
                      <span key="when" className={styles["when"]}>
                          {dayjs(startDate).fromNow()}
                      </span>
                  ]
                : []),
            ...(isScheduledLive || isScheduledUpload
                ? [
                      <span key="premiering">
                          {isScheduledLive
                              ? t("widgets:Scheduled-Live")
                              : t("widgets:Premiering")}{" "}
                          {dayOfBroadcast
                              ? `${t("widgets:at")} ${localizedTime}`
                              : dayjs(startDate).format(
                                    "dddd, MMMM DD, YYYY hh:mm A"
                                )}
                      </span>
                  ]
                : []),
            ...(exists(video) &&
            exists(video?.Details?.Duration) &&
            !(
                video?.Details?.Duration === -1 && !broadcast?.Details?.InputId
            ) &&
            location !== "practice"
                ? [
                      <span key="duration" className={styles["duration"]}>
                          {video?.Details?.Duration === -1
                              ? t("widgets:currently-live")
                              : formatTimeDuration(video?.Details?.Duration)}
                      </span>
                  ]
                : []),
            ...(exists(metrics)
                ? [
                      <span key="views" className={styles["views"]}>
                          {metrics.Details?.TotalViews}{" "}
                          {location === "player-playlist"
                              ? metrics.Details?.TotalViews === 1
                                  ? hasCatalogClaim
                                      ? t("widgets:view-in-collection")
                                      : t("widgets:view-in-player")
                                  : hasCatalogClaim
                                  ? t("widgets:views-in-collection")
                                  : t("widgets:views-in-player")
                              : t("widgets:total-views")}
                      </span>
                  ]
                : []),
            ...(location === "practice"
                ? [
                      <span key="expires" className={styles["when"]}>
                          {t("widgets:Expires")}{" "}
                          {dayjs(
                              broadcast?.Details?.InputEffectiveDeletionDate
                          ).format("dddd, MMMM DD, YYYY hh:mm A")}
                      </span>
                  ]
                : [])
        ],
        [
            isScheduledUpload,
            isScheduledLive,
            startDate,
            video,
            broadcast?.Details?.InputEffectiveDeletionDate,
            broadcast?.Details?.InputId,
            t,
            metrics,
            location,
            hasCatalogClaim,
            isUnpublished,
            dayOfBroadcast,
            localizedTime
        ]
    );

    const isTooBigToDownload = useMemo<boolean>(() => {
        // Cannot download videos that are over 4 hours
        return video?.Details?.Duration > 14400;
    }, [video?.Details?.Duration]);

    const downloadButton = useMemo(() => {
        if (!showDownload) return null;
        return (
            <Button
                type="icon"
                onClick={() =>
                    handleDownload({
                        broadcastId: broadcast.Details.Id,
                        videoId: video?.Details?.Uid,
                        title: broadcast.Details.Title
                    })
                }
                showProgress={downloadStatus === VideoDownloadStatus.InProgress}
                progress={percentComplete}
                disabled={disabled || isTooBigToDownload}
                title={
                    isTooBigToDownload
                        ? t("video-library:too-big-to-download")
                        : t("buttons:download")
                }
            >
                <DownloadIcon />
            </Button>
        );
    }, [
        showDownload,
        handleDownload,
        broadcast.Details.Id,
        video?.Details?.Uid,
        broadcast.Details.Title,
        downloadStatus,
        percentComplete,
        disabled,
        isTooBigToDownload,
        t
    ]);

    const isDisabled = disabled || isVideoProcessing;
    const localDisabledVariant = isVideoProcessing
        ? DisabledVariant.Processing
        : disabledVariant;

    return (
        <>
            {!!broadcast && (
                <>
                    <div className={styles["broadcast-details"]}>
                        <BaseDetails
                            variant={variant}
                            isDraggable={isDraggable}
                            disabled={isDisabled}
                            disabledVariant={localDisabledVariant}
                            thumbnail={
                                <BroadcastThumbnail
                                    thumbnailImageURL={thumbnailImageURL}
                                    allowVideoPlaybackOnThumbnailClick={
                                        allowVideoPlaybackOnThumbnailClick
                                    }
                                    handlePlayVideo={() => {
                                        trackEvent("Thumbnail Video Playback", {
                                            broadcastId: broadcast?.Details?.Id,
                                            location
                                        });
                                        setVideoPlaybackModalIsOpen(true);
                                    }}
                                    isScheduledUpload={isScheduledUpload}
                                    isScheduledLive={isScheduledLive} // If true shows countdown. Set to false to accommodate for showing thumbnail on new livestreams page. This prop can be removed after release
                                    showPremiereCountdown={
                                        showPremiereCountdown
                                    }
                                    scheduledSecondsRemaining={
                                        timeRemaining ?? 0
                                    }
                                    disabled={isDisabled}
                                    disabledVariant={localDisabledVariant}
                                />
                            }
                            title={shorten(
                                `${broadcast?.Details?.Title}${
                                    location === "practice"
                                        ? ` ${dayjs(startDate).format(
                                              "MM/DD/YY hh:mm A"
                                          )}`
                                        : ""
                                }`,
                                60
                            )}
                            detailRows={
                                <>
                                    {isDisabled &&
                                    localDisabledVariant ===
                                        DisabledVariant.Disabled ? (
                                        <p className={styles["disabled"]}>
                                            {t("broadcast-details:disabled")}
                                        </p>
                                    ) : (
                                        <>
                                            <DetailRow variant="info-row">
                                                <ComponentMap
                                                    items={
                                                        broadcastDetailsItems
                                                    }
                                                    element={(i) => i}
                                                    separator={
                                                        <span>{"•"}</span>
                                                    }
                                                />
                                            </DetailRow>
                                            <DetailRow variant="badge-row">
                                                {badges ? (
                                                    <VideoBadgesV2
                                                        broadcast={broadcast}
                                                        entitlements={
                                                            entitlements
                                                        }
                                                        groupEntitlements={
                                                            location ===
                                                            "video-library"
                                                        }
                                                        shownEntitlements={[
                                                            "all"
                                                        ]}
                                                    />
                                                ) : null}
                                            </DetailRow>
                                        </>
                                    )}
                                </>
                            }
                            actionItems={
                                <>
                                    {showEdit && (
                                        <>
                                            {!mobileCheck.isMobile ? (
                                                <>
                                                    <Button
                                                        type="icon"
                                                        onClick={() =>
                                                            onEdit?.(broadcast)
                                                        }
                                                        disabled={disabled}
                                                        title={t(
                                                            "buttons:edit"
                                                        )}
                                                    >
                                                        <PencilIcon
                                                            className={
                                                                styles[
                                                                    "btn-edit"
                                                                ]
                                                            }
                                                        />
                                                    </Button>
                                                    {location ===
                                                        "video-library" && (
                                                        <ContextMenuWrapper
                                                            items={shareOptions}
                                                            dropDirection="up"
                                                        >
                                                            <Button
                                                                type="icon"
                                                                disabled={
                                                                    disabled ||
                                                                    !shareable
                                                                }
                                                                title={t(
                                                                    "buttons:share"
                                                                )}
                                                            >
                                                                <ShareIcon />
                                                            </Button>
                                                        </ContextMenuWrapper>
                                                    )}
                                                    {downloadButton}
                                                    {location ===
                                                    "video-library" ? (
                                                        <Button
                                                            type="icon"
                                                            onClick={
                                                                handleDeleteBroadcast
                                                            }
                                                            title={t(
                                                                "buttons:delete"
                                                            )}
                                                        >
                                                            <TrashIcon
                                                                className={
                                                                    styles[
                                                                        "trash-icon"
                                                                    ]
                                                                }
                                                            />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            type="remove"
                                                            onClick={
                                                                handleDeleteBroadcast
                                                            }
                                                            title={t(
                                                                "buttons:remove"
                                                            )}
                                                        >
                                                            {t(
                                                                "video-player-settings:remove"
                                                            )}
                                                        </Button>
                                                    )}
                                                </>
                                            ) : downloadStatus ===
                                              VideoDownloadStatus.InProgress ? (
                                                <span
                                                    className={
                                                        styles[
                                                            "download-button-wrapper"
                                                        ]
                                                    }
                                                >
                                                    {downloadButton}
                                                </span>
                                            ) : (
                                                <ContextMenu
                                                    dropDirection="up"
                                                    items={
                                                        mobileButtonsMenuItems
                                                    }
                                                />
                                            )}
                                        </>
                                    )}

                                    <VideoPlaybackModal
                                        broadcastId={broadcast?.Details?.Id}
                                        isOpen={videoPlaybackModalIsOpen}
                                        setIsOpen={setVideoPlaybackModalIsOpen}
                                    />

                                    {exists(gatedContentStatus) &&
                                        gatedContentStatus !==
                                            GatedContentStatus.FEATURE_DISABLED &&
                                        showPricing && (
                                            <div
                                                className={
                                                    styles[
                                                        "price-selection-container"
                                                    ]
                                                }
                                            >
                                                <GatedContentDisabledTooltip
                                                    gatedContentStatus={
                                                        gatedContentStatus
                                                    }
                                                >
                                                    <Button
                                                        disabled={
                                                            gatedContentStatus !==
                                                            GatedContentStatus.READY
                                                        }
                                                        type="badge"
                                                        isActive={exists(
                                                            productEntitlement
                                                        )}
                                                        onClick={() => {
                                                            setExistingEntitlement(
                                                                productEntitlement
                                                            );
                                                            setPricingSelectModalIsOpen(
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        {exists(
                                                            productEntitlement
                                                        )
                                                            ? productEntitlement
                                                                  ?.Product
                                                                  ?.Details
                                                                  ?.Name
                                                            : t(
                                                                  "video-player-settings:add-pricing"
                                                              )}
                                                    </Button>
                                                </GatedContentDisabledTooltip>
                                            </div>
                                        )}
                                </>
                            }
                        ></BaseDetails>
                    </div>
                </>
            )}
        </>
    );
};
